import {useState, useEffect, useCallback, useRef} from '@wordpress/element';
import {getSetting} from '@woocommerce/settings';
import {isFieldRequired} from '@ppcp/utils';

export const useGooglePayButton = (
    {
        onClick,
        onClose,
        onError,
        fields,
        settings,
        paymentData,
        needsShipping,
        paymentsClient,
        setPaymentData,
        buttonContainer,
    }) => {
    const [button, setButton] = useState(null);
    const currentData = useRef({settings});
    const data = getSetting('ppcp_googlepay_data');

    useEffect(() => {
        currentData.current = {
            ...currentData.current,
            settings
        }
    }, [settings]);

    /**
     * Reset button when payment data is cleared
     */
    useEffect(() => {
        if (!paymentData || Object.keys(paymentData).length === 0) {
            setButton(null);
        }
    }, [paymentData]);

    /**
     * Handle button click - initiates Google Pay payment flow
     */
    const onButtonClick = useCallback(async () => {
        try {
            if (onClick) {
                onClick();
            }

            // Build payment data request
            const paymentDataRequest = buildPaymentDataRequest({
                fields,
                settings,
                needsShipping
            });

            // Load payment data from Google Pay
            const paymentData = await paymentsClient.loadPaymentData(paymentDataRequest);

            // Payment data will be processed via onPaymentAuthorized callback
            // Just store it for now
            if (settings.isExpress) {
                setPaymentData({
                    paymentMethodData: paymentData.paymentMethodData,
                    email: paymentData.email,
                    shippingAddress: paymentData.shippingAddress,
                    needsShipping
                }, true);
            } else {
                setPaymentData({
                    paymentMethodData: paymentData.paymentMethodData
                }, false);
            }

        } catch (error) {
            if (error?.statusCode === 'CANCELED') {
                if (onClose) {
                    onClose();
                }
                // User canceled, don't show error
                return;
            }
            if (onError) {
                onError(error?.statusMessage || error?.message || error);
            }
        }
    }, [
        onClick,
        onClose,
        onError,
        settings,
        needsShipping,
        paymentsClient
    ]);

    /**
     * Create and render Google Pay button
     */
    useEffect(() => {
        if (paymentsClient && settings?.config && buttonContainer && !button) {
            const {settings} = currentData.current;
            try {
                const googlePayButton = paymentsClient.createButton({
                    onClick: onButtonClick,
                    buttonBorderType: data.button.buttonBorderType,
                    buttonColor: data.button.buttonColor,
                    buttonType: data.button.buttonType,
                    buttonRadius: data.button.buttonRadius,
                    buttonLocale: data.button.buttonLocale,
                    buttonSizeMode: settings.isExpress ? 'fill' : data.button.buttonSizeMode,
                });
                buttonContainer.innerHTML = '';
                buttonContainer.appendChild(googlePayButton);
                setButton(googlePayButton);
            } catch (error) {
                console.error('Failed to create Google Pay button:', error);
                onError(error);
            }
        }

    }, [
        button,
        onError,
        onButtonClick,
        paymentsClient,
        buttonContainer
    ]);

    return {
        button
    };
};

/**
 * Build payment data request for Google Pay
 */
const buildPaymentDataRequest = (
    {
        fields,
        settings,
        needsShipping
    }) => {
    const {
        merchantInfo,
        apiVersion,
        apiVersionMinor,
        allowedPaymentMethods
    } = settings.config;

    // Set phoneNumberRequired based on WooCommerce field requirements
    if (allowedPaymentMethods[0]?.parameters?.billingAddressParameters) {
        allowedPaymentMethods[0].parameters.billingAddressParameters.phoneNumberRequired = fields?.phone?.required || false;
    }

    const request = {
        apiVersion,
        apiVersionMinor,
        merchantInfo,
        allowedPaymentMethods,
        transactionInfo: {
            countryCode: settings.country_code,
            currencyCode: settings.currency_code,
            totalPriceStatus: 'FINAL',
            totalPrice: settings.total_price,
            displayItems: settings.display_items || [],
            totalPriceLabel: settings.i18n?.total_price_label || 'Total'
        },
        emailRequired: fields?.email?.required || false,
        callbackIntents: ['PAYMENT_AUTHORIZATION']
    };

    // Add shipping if needed
    if (needsShipping && settings.isExpress) {
        request.shippingAddressRequired = true;
        request.shippingOptionRequired = true;
        request.shippingAddressParameters = {};
        request.callbackIntents.push('SHIPPING_ADDRESS', 'SHIPPING_OPTION');

        if (settings.shipping_options && settings.shipping_options.length > 0) {
            request.shippingOptionParameters = {
                shippingOptions: settings.shipping_options,
                ...(settings.shipping_method && {
                    defaultSelectedOptionId: settings.shipping_method
                })
            };
        }
    }

    return request;
};

export default useGooglePayButton;